import Button, { BUTTON_COLOR, BUTTON_VARIANT } from '@ab-core/button';
import { ROUTES } from '@ab-core/functions';
import type { ProductBase } from '@ab-core/graphql/dist';
import AddLineItem from '@ab-core/hooks/cartNew/LineItems/addToCart/addToCart';
import getActiveCart from '@ab-core/hooks/cartNew/activeCart/useActiveCart';
import { Link } from '@ab-core/link';
import { Loader } from '@ab-core/loader';
import useText from '@ab-core/localization/src/useText';
import type { ModalType } from '@ab-core/modal';
import Modal from '@ab-core/modal';
import Spacer from '@ab-core/spacer';
import { cartStore } from '@ab-core/store';
import { getTestIdProp } from '@ab-core/testing';
import Text, { FontTypes } from '@ab-core/text';
import { toast } from '@ab-core/toast';
import type { FC } from 'react';
import React, { useCallback, useEffect } from 'react';

const PLACEHOLDER_SPLITT = '$placeholder';

type ProductRelationModalProps = Pick<ProductBase, 'sku' | 'successorProduct'> &
    Pick<ModalType, 'open' | 'onClose'> & {
        primaryButtonClicked?: () => void;
        quantity: number;
    };

export const ProductRelationModal: FC<ProductRelationModalProps> = (props) => {
    const { open, onClose, successorProduct, sku, quantity, primaryButtonClicked } = props;

    const { activeCart } = getActiveCart();
    const { addLineItem, success, pending, error } = AddLineItem(activeCart?.id || '');

    const headline = useText('productRelationModal.headline');
    const text = useText('productRelationModal.text');
    const primaryButtonText = useText('productRelationModal.buttonPrimary');
    const secondaryButtonText = useText('productRelationModal.buttonSecondary');
    const linkText = useText('productRelationModal.linkText');
    const productAddedToCartNotification = useText('cartList.productAddedToCartNotification').split(PLACEHOLDER_SPLITT);

    const formattedPrimaryText = primaryButtonText
        ? primaryButtonText.replace('$placeholder', successorProduct || '')
        : `Nachfolger (${successorProduct}) in den Warenkorb`;

    useEffect(() => {
        if (!success) {
            return;
        }

        if (!activeCart?.name) {
            return;
        }

        cartStore.setters.setCartButtonDisabled(false);

        toast.success(
            'Warenkorb aktualisiert',
            <Text
                type={FontTypes.Subtitle}
                color="gray70"
                {...getTestIdProp('toast-success-artikel-hinzugefügt-message')}
            >
                {productAddedToCartNotification[0]}
                <b>{activeCart?.name}</b>
                {productAddedToCartNotification[1]}
            </Text>
        );

        if (primaryButtonClicked) {
            primaryButtonClicked();
        }

        onClose();
    }, [success]);

    useEffect(() => {
        if (error) {
            toast.error('Eingabe konnte nicht gespeichert werden');
        }
    }, [error]);

    const addProductToCart = useCallback(
        (productSku: string) => {
            if (!activeCart?.id) {
                return;
            }

            cartStore.setters.setCartButtonDisabled(true);
            addLineItem({
                variables: {
                    cartIds: activeCart.id,
                    lineItemInput: [{ quantity, sku: productSku, fields: [], id: undefined }]
                }
            });
        },
        [activeCart?.id, quantity, addLineItem]
    );

    const handleAddSuccessor = useCallback(() => {
        if (successorProduct) {
            addProductToCart(successorProduct);
        }
    }, [successorProduct, addProductToCart]);

    const handleAddProduct = useCallback(() => {
        if (sku) {
            addProductToCart(sku);
        }
    }, [sku, addProductToCart]);

    return (
        <Modal open={open} onClose={onClose} headerText={headline}>
            {pending && <Loader />}
            <Spacer size="xs" />
            <div className="flex flex-direction-column gap-medium3">
                <Text content={text} />
                <Link to={`${ROUTES.PRODUCTPAGE}/${successorProduct}`} className="text-color-primary" onClick={onClose}>
                    {linkText}
                </Link>
                <div className="flex flex-direction-row gap-small3">
                    <Button onClick={handleAddSuccessor} disabled={pending} fluid>
                        {formattedPrimaryText}
                    </Button>
                    <Button
                        onClick={handleAddProduct}
                        variant={BUTTON_VARIANT.OUTLINE}
                        color={BUTTON_COLOR.PRIMARY}
                        disabled={pending}
                        fluid
                    >
                        {secondaryButtonText}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};
